import React from 'react';
import './VisibilityReportPreview.scss';
import { useTranslation } from 'react-i18next';
import { ChatType } from '../../../shared/types/monitor/ChatType';
import { modelIcon } from '../../../shared/components/report/modelIcon';

/**
 * Schemat slajdu w podglądzie — `shape` steruje wyłącznie rysunkiem-atrapą, żeby użytkownik poznał
 * sekcję po układzie, a nie po samym tytule.
 */
const SECTIONS: Array<{ titleKey: string; accentKey: string; shape: 'kpi' | 'bars' | 'pie' | 'grid' | 'rows' }> = [
  { titleKey: 'reportSummaryTitle', accentKey: 'reportSummaryTitleAccent', shape: 'kpi' },
  { titleKey: 'reportPlatformTitle', accentKey: 'reportPlatformTitleAccent', shape: 'rows' },
  { titleKey: 'reportRankingTitle', accentKey: 'reportRankingTitleAccent', shape: 'bars' },
  { titleKey: 'reportSovTitle', accentKey: 'reportSovTitleAccent', shape: 'pie' },
  { titleKey: 'reportMatrixTitle', accentKey: 'reportMatrixTitleAccent', shape: 'grid' },
  { titleKey: 'reportDomainsTitle', accentKey: 'reportDomainsTitleAccent', shape: 'rows' },
  { titleKey: 'reportPagesTitle', accentKey: 'reportPagesTitleAccent', shape: 'rows' },
  { titleKey: 'reportPromptsTitle', accentKey: 'reportPromptsTitleAccent', shape: 'grid' },
];

const BAR_HEIGHTS = [90, 74, 61, 47, 33];

interface VisibilityReportPreviewProps {
  brand: string;
  /** `YYYY-MM-DD – YYYY-MM-DD`; puste, dopóki zakres nie jest kompletny. */
  periodLabel: string;
  /** Modele w wyborze — okładka pokazuje ich logotypy, tak jak zrobi to raport. */
  models: ChatType[];
  promptsCount: number;
}

/** Atrapa raportu w oknie generowania: co dostanie klient, zanim policzymy cokolwiek. */
const VisibilityReportPreview: React.FC<VisibilityReportPreviewProps> = ({
  brand,
  periodLabel,
  models,
  promptsCount,
}) => {
  const { t } = useTranslation();

  const shape = (kind: string) => {
    if (kind === 'kpi') {
      return (
        <div className="visibility-report-preview__kpis">
          {[0, 1, 2, 3].map((index) => (
            <span key={index} className="visibility-report-preview__kpi" />
          ))}
        </div>
      );
    }
    if (kind === 'bars') {
      return (
        <div className="visibility-report-preview__bars">
          {BAR_HEIGHTS.map((height, index) => (
            <span
              key={index}
              className={`visibility-report-preview__bar${
                index === 0 ? ' visibility-report-preview__bar--tracked' : ''
              }`}
              style={{ height: `${height}%` }}
            />
          ))}
        </div>
      );
    }
    if (kind === 'pie') {
      return <span className="visibility-report-preview__pie" />;
    }
    if (kind === 'grid') {
      return (
        <div className="visibility-report-preview__grid">
          {Array.from({ length: 12 }, (_, index) => (
            <span key={index} className="visibility-report-preview__cell" />
          ))}
        </div>
      );
    }
    return (
      <div className="visibility-report-preview__rows">
        {[100, 78, 56, 40].map((width, index) => (
          <span key={index} className="visibility-report-preview__row" style={{ width: `${width}%` }} />
        ))}
      </div>
    );
  };

  return (
    <div className="visibility-report-preview">
      <div className="visibility-report-preview__paper">
        <div className="visibility-report-preview__cover">
          <span className="visibility-report-preview__logo" />
          <span className="visibility-report-preview__brand">{brand}</span>
          <span className="visibility-report-preview__subtitle">{t('reportCoverSubtitle')}</span>
          <span className="visibility-report-preview__period">{periodLabel || '—'}</span>
          <div className="visibility-report-preview__stats">
            <span className="visibility-report-preview__stat">
              {promptsCount} {t('prompts')}
            </span>
            <span className="visibility-report-preview__stat">
              {models.length} {t('models')}
              <span className="visibility-report-preview__icons">
                {models.map((model) => {
                  const icon = modelIcon(model);
                  return icon ? <span key={model} className={`${icon} ico--xs`} /> : null;
                })}
              </span>
            </span>
          </div>
        </div>

        {SECTIONS.map((section) => (
          <div key={section.titleKey} className="visibility-report-preview__section">
            <span className="visibility-report-preview__section-title">
              {t(section.titleKey)} {t(section.accentKey)}
            </span>
            {shape(section.shape)}
          </div>
        ))}
      </div>
    </div>
  );
};

export default VisibilityReportPreview;
